import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import {
  FieldType,
  FieldTypeConfig,
  FormlyFieldConfig,
  FormlyModule,
} from '@ngx-formly/core';
import {
  FormlyFieldSelectProps,
  FormlySelectModule,
} from '@ngx-formly/core/select';
import { FormlyFieldProps } from '@ngx-formly/primeng/form-field';
import { ScrollerOptions } from 'primeng/api';
import { ChipModule } from 'primeng/chip';
import { InputTextModule } from 'primeng/inputtext';
import {
  Select,
  SelectFilterEvent,
  SelectLazyLoadEvent,
  SelectModule,
} from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  filter,
  Observable,
  skip,
  startWith,
  Subject,
  Subscription,
  take,
  takeUntil,
} from 'rxjs';

interface SelectLazyProps extends FormlyFieldProps, FormlyFieldSelectProps {
  query: (
    parPage: number,
    page: number,
    search?: string,
    filters?: string,
    model?: any,
  ) => Observable<{ data: unknown[]; meta: { total: number; to: number } }>;
  pageSize?: number;
  filterPlaceholder?: string;
  getValueCondition?: (value: any) => string;
  initValueField?: string;
  mapLabel?: (item: any) => string;
  allowCustomValue?: boolean;
  hideFilter?: boolean;
  showClear?: boolean;
}

export interface FormlySelectLazyFieldConfig
  extends FormlyFieldConfig<SelectLazyProps> {}

interface SelectLazyItem {
  label?: string;
  value?: any;
  disabled?: boolean;
  status?: 'pending' | 'loading' | 'loaded';
  skeletonWidth?: number;
  chips?: {
    label: string;
    icon?: string;
    color?: string;
  }[];
}

@Component({
  selector: 'kon-select-lazy-type',
  imports: [
    CommonModule,
    FormlyModule,
    FormlySelectModule,
    ReactiveFormsModule,
    InputTextModule,
    SkeletonModule,
    ChipModule,
    SelectModule,
  ],
  template: `
    <p-select
      #select
      [id]="id"
      [appendTo]="'body'"
      [options]="(items$ | async) ?? []"
      [optionLabel]="'label'"
      [optionValue]="'value'"
      [formControl]="formControl"
      [showClear]="!props.required || props.showClear"
      (onChange)="props.change && props.change(field, $event)"
      [filter]="props.allowCustomValue ? false : !props.hideFilter"
      [autofocusFilter]="true"
      [resetFilterOnHide]="true"
      [style]="{
        width: '100%',
        height: 'var(--input-height)',
        'min-height': '37px',
      }"
      [virtualScroll]="true"
      [virtualScrollItemSize]="42"
      [virtualScrollOptions]="scrollerOptions"
      (onShow)="onShow()"
      (onHide)="onHide()"
      (onFilter)="onSearch($event)"
      [filterMatchMode]="$any('lazy')"
      [placeholder]="props.filterPlaceholder ?? ''"
      [editable]="props.allowCustomValue"
    >
      <ng-template let-item #selectedItem>
        @if (item) {
          <div class="flex items-center gap-2 max-w-max">
            <div [class]="itemClasses(item, 'truncate')" [title]="item.label">
              {{ item.label }}
            </div>
            @for (chip of item?.chips ?? []; track $index) {
              <p-chip
                [label]="chip.label"
                [icon]="chip.icon"
                [style]="{ backgroundColor: chip.color }"
                class="-my-2"
              />
            }
          </div>
        }
      </ng-template>
      <ng-template let-item pTemplate="item">
        <div class="flex items-center gap-2 max-w-full grow">
          @if (item.status === 'loaded') {
            <div [class]="itemClasses(item, 'grow truncate ')">
              {{ item.label }}
            </div>

            @for (chip of item?.chips ?? []; track $index) {
              <p-chip
                [label]="chip.label"
                [icon]="chip.icon"
                [style]="{ backgroundColor: chip.color }"
                class="-my-2"
              />
            }
          } @else {
            <p-skeleton
              class="w-full"
              [width]="(item.skeletonWidth ?? 50) + '%'"
              height="1rem"
            ></p-skeleton>
          }
        </div>
      </ng-template>
    </p-select>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlySelectLazyTypeComponent
  extends FieldType<FieldTypeConfig<SelectLazyProps>>
  implements AfterViewInit, OnDestroy
{
  @ViewChild('select') select!: Select;

  scrollerOptions: ScrollerOptions = {
    showLoader: true,
    lazy: true,
    onLazyLoad: this.onLazyLoad.bind(this),
    scrollHeight: '220px',
  };

  items$ = new BehaviorSubject<SelectLazyItem[]>([]);
  search$ = new BehaviorSubject<string>('');
  destroyed$ = new Subject<void>();

  private lazyLoadTrigger$ = new Subject<SelectLazyLoadEvent>();

  private searchSubscription: Subscription = Subscription.EMPTY;
  private valueSubscription: Subscription = Subscription.EMPTY;
  private freshLoadSubscription: Subscription = Subscription.EMPTY;
  private lazyLoadSubscriptions: Subscription[] = [];

  ngAfterViewInit(): void {
    if (this.select?.filterService) {
      // this disables default filtering
      this.select.filterService.register('lazy', () => true);
    }

    this.formControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        distinctUntilChanged(),
        startWith(this.formControl.value),
      )
      .subscribe((value) => {
        if (!this.props.allowCustomValue && value) {
          const hasValueInOptions = this.items$.value.find(
            (item) => item.value === value,
          );

          if (!hasValueInOptions) {
            this.loadValue(value);
          }
        }

        if (this.props.allowCustomValue) {
          this.search$.next(value ?? '');
        }
      });

    this.lazyLoadTrigger$
      .pipe(
        takeUntil(this.destroyed$),
        filter(({ first, last }) => {
          const allLoaded = this.items$.value
            .slice(first, last)
            .every((item) => item?.status === 'loaded');

          return !allLoaded;
        }),
      )
      .subscribe(({ first, last }) => {
        const firstPendingIndex = this.items$.value.findIndex(
          (item, index) =>
            index >= first && index < last && item?.status === 'pending',
        );

        const firstLoadedIndex = this.items$.value.findIndex(
          (item, index) =>
            index >= firstPendingIndex &&
            index < last &&
            item?.status === 'loaded',
        );

        if (firstPendingIndex === -1) {
          return;
        }

        // const itemsLimit = last - firstPendingIndex;
        const pageSize =
          firstLoadedIndex !== -1
            ? Math.min(firstLoadedIndex - firstPendingIndex, this.pageSize())
            : this.pageSize();
        const pageToLoad = Math.floor(firstPendingIndex / pageSize) + 1;

        const items = this.items$.value.slice(
          firstPendingIndex,
          firstPendingIndex + pageSize,
        );

        this.patchItems(
          firstPendingIndex,
          items.map((item) => ({
            ...this.mapItem(item),
            status: item.status === 'loaded' ? 'loaded' : 'loading', // don't set to loading if already loaded to prevent flickering
          })),
          this.items$.value.length,
        );

        const subscription = this.props
          .query(pageSize, pageToLoad, this.search$.value)
          .pipe(take(1))
          .subscribe((response) => {
            this.patchItems(
              firstPendingIndex,
              response.data.map((item) => ({
                ...this.mapItem(item),
                status: 'loaded',
              })),
              response.meta.total,
            );

            this.lazyLoadSubscriptions = this.lazyLoadSubscriptions.filter(
              (sub) => sub !== subscription,
            );
          });

        this.lazyLoadSubscriptions.push(subscription);
      });
  }

  loadValue(itemValue: unknown = undefined) {
    this.select?.scroller?.scrollToIndex(0);
    this.valueSubscription.unsubscribe();

    if (itemValue && this.props.initValueField) {
      // option by mal uz byt na modeli (napr. ako relation)
      const initValue = this.model[this.props.initValueField];

      if (initValue) {
        if (this.props.mapLabel && typeof this.props.labelProp === 'string') {
          initValue[this.props.labelProp] = this.props.mapLabel(initValue);
        }

        const item: SelectLazyItem = {
          ...this.mapItem(initValue),
          status: 'loaded',
        };

        return this.patchItems(0, [item], 1);
      }
    }

    const filter = itemValue
      ? this.props.getValueCondition
        ? this.props.getValueCondition(itemValue)
        : `${this.props.valueProp} eq "${itemValue}"`
      : undefined;

    this.valueSubscription = this.props
      .query(this.pageSize(), 1, undefined, filter, this.model)
      .subscribe((response) => {
        const items = response.data.map((item) => ({
          ...this.mapItem(item),
          status: 'loaded',
        }));

        this.patchItems(0, items, items.length);
      });
  }

  freshLoad(search = '') {
    this.select?.scroller?.scrollToIndex(0);

    this.lazyLoadSubscriptions.forEach((sub) => sub.unsubscribe());
    this.lazyLoadSubscriptions = [];

    this.freshLoadSubscription.unsubscribe();
    this.freshLoadSubscription = this.props
      .query(this.pageSize(), 1, search, undefined, this.model)
      .subscribe((response) => {
        this.patchItems(
          0,
          response.data.map((item) => ({
            ...this.mapItem(item),
            status: 'loaded',
          })),
          response.meta.total,
        );

        this.select?.focusedOptionIndex.set(0); // focus first item
      });
  }

  onLazyLoad(event: SelectLazyLoadEvent) {
    this.lazyLoadTrigger$.next(event);
  }

  onSearch(event: SelectFilterEvent) {
    if (event.filter === this.search$.value) return;
    this.search$.next(event.filter);

    const valueOption = this.items$.value.find(
      (item) => item.value === this.formControl.value,
    );

    const placeholderItems: SelectLazyItem[] = [
      valueOption,
      ...this.generateItems(5),
    ].filter((item): item is SelectLazyItem => !!item);

    this.items$.next(placeholderItems);
  }

  patchItems(start: number, items: any[], total: number) {
    let newItems = [...this.items$.value];

    if (newItems.length != total) {
      newItems = this.generateItems(total);
    }

    let idx = start;

    for (const item of items) {
      newItems[idx] = {
        ...newItems[idx],
        ...item,
      };
      idx++;
    }

    this.items$.next(newItems);
  }

  onShow() {
    this.freshLoad();
    this.searchSubscription.unsubscribe();
    this.searchSubscription = this.search$
      .pipe(skip(1), debounceTime(500))
      .subscribe((search) => {
        this.freshLoad(search);
      });
  }

  onHide() {
    this.search$.next('');
    this.cleanupSubscriptions();
  }

  ngOnDestroy() {
    this.cleanupSubscriptions();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  pageSize = () => this.props.pageSize ?? 100;

  private generateItems(length: number): SelectLazyItem[] {
    return Array.from({ length }, () => ({
      disabled: true,
      status: 'pending',
      skeletonWidth: Math.floor(Math.random() * 41) + 40, // random width for skeleton from 40% to 80%
    }));
  }

  private mapItem(item: any): Partial<SelectLazyItem> {
    const label = this.getValue(item, this.props.labelProp ?? 'label') ?? '';
    const value = this.getValue(item, this.props.valueProp ?? 'value');
    const disabled = this.getValue(item, this.props.disabledProp ?? 'disabled');

    return {
      ...item,
      label,
      value,
      disabled,
    };
  }

  private getValue(item: any, prop: string | ((item: any) => any)) {
    if (typeof prop === 'string') {
      return item?.[prop];
    } else if (typeof prop === 'function') {
      return prop(item);
    } else {
      return undefined;
    }
  }

  private cleanupSubscriptions() {
    this.searchSubscription?.unsubscribe();
    this.valueSubscription?.unsubscribe();
    this.freshLoadSubscription?.unsubscribe();
    this.lazyLoadSubscriptions.forEach((sub) => sub.unsubscribe());
    this.lazyLoadSubscriptions = [];
  }

  itemClasses(item: any, className: string) {
    const classes = [className];
    if (item?.deletedAt) {
      classes.push('line-through');
    }
    return classes.join(' ');
  }
}
